// Calendar view + associated list components for the /events page.
// Extracted from pages/Events.jsx during Phase-5 Task C refactor so the
// orchestrator stays lean.
import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin,
  CalendarDays,
  Clock,
  DollarSign,
  CheckCircle2,
  AlertCircle,
  X,
  Heart,
  Square,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  EVENT_TYPES,
  formatDate,
  formatEventDates,
  formatCurrency,
  getDaysUntil,
} from '../../lib/utils';
import {
  PRIORITY_COLORS,
  sortEventsByPriority,
} from './eventPriority';

export const EventListItem = ({ event, distance, showMap, onFavoriteChange, applicationStatus, isFavorited }) => {
  const eventType = EVENT_TYPES[event.event_type] || { label: event.event_type, color: 'gray' };
  const daysUntilDeadline = getDaysUntil(event.deadline);
  
  // Determine priority styling with per-status granularity
  const isMyEvent = event.is_user_event;
  const isCommunity = event.is_community;
  
  let priorityConfig = PRIORITY_COLORS.community;
  let priorityLabel = '';
  let showPulse = false;
  
  if (applicationStatus) {
    if (applicationStatus.status === 'accepted') {
      priorityConfig = PRIORITY_COLORS.accepted;
      priorityLabel = 'Accepted';
    } else if (['draft', 'pending', 'submitted'].includes(applicationStatus.status)) {
      priorityConfig = PRIORITY_COLORS.applicationInProgress;
      priorityLabel = applicationStatus.status === 'submitted' ? 'Submitted' : 'In Progress';
      showPulse = true;
    } else if (applicationStatus.status === 'waitlisted') {
      priorityConfig = PRIORITY_COLORS.waitlisted;
      priorityLabel = 'Waitlisted';
    } else if (applicationStatus.status === 'rejected') {
      priorityConfig = PRIORITY_COLORS.rejected;
      priorityLabel = 'Rejected';
    }
  } else if (isFavorited) {
    priorityConfig = PRIORITY_COLORS.saved;
    priorityLabel = 'Saved';
  } else if (isMyEvent) {
    priorityConfig = PRIORITY_COLORS.myEvent;
    priorityLabel = 'My Event';
  } else if (isCommunity) {
    priorityConfig = PRIORITY_COLORS.community;
    priorityLabel = 'Community';
  }

  return (
    <Link 
      to={`/events/${event.id}`}
      className={`card-dashboard flex flex-col sm:flex-row gap-4 hover:shadow-hard-hover hover:-translate-y-1 transition-transform border-l-4 ${priorityConfig.border}`}
      data-testid={`event-list-item-${event.id}`}
    >
      <div className="flex-1">
        <div className="flex flex-wrap items-start gap-2 mb-2">
          {/* Priority Badge */}
          {priorityLabel && (
            <span className={`px-2 py-1 text-xs font-bold rounded-full border flex items-center gap-1 ${priorityConfig.badge}`}>
              {showPulse && (
                <span className={`w-2 h-2 rounded-full ${priorityConfig.dot} animate-pulse`} />
              )}
              {applicationStatus?.status === 'accepted' && <CheckCircle2 className="w-3 h-3" />}
              {applicationStatus?.status === 'rejected' && <X className="w-3 h-3" />}
              {applicationStatus?.status === 'waitlisted' && <AlertCircle className="w-3 h-3" />}
              {priorityLabel}
            </span>
          )}
          
          <span className={`badge-category badge-${event.event_type}`}>
            {eventType.label}
          </span>
          
          {daysUntilDeadline !== null && daysUntilDeadline >= 0 && daysUntilDeadline <= 14 && (
            <span className="px-2 py-1 bg-[#FE5F55] text-white text-xs font-bold rounded-full">
              {daysUntilDeadline === 0 ? 'Deadline Today!' : `${daysUntilDeadline}d to apply`}
            </span>
          )}
          {daysUntilDeadline !== null && daysUntilDeadline < 0 && (
            <span className="px-2 py-1 bg-rose-100 text-rose-700 text-xs font-bold rounded-full border border-rose-400 flex items-center gap-1" data-testid={`list-deadline-passed-${event.id}`}>
              <X className="w-3 h-3" />
              Deadline Passed
            </span>
          )}
        </div>
        
        <h3 className="font-bold text-lg text-[#1E3A5F] mb-2 font-['Outfit']">
          {event.title}
        </h3>
        
        <div className="flex flex-wrap gap-4 text-sm text-[#4A4A4A]">
          <div className="flex items-center gap-1">
            <MapPin className="w-4 h-4 text-[#FE5F55]" />
            {event.city}, {event.state}
          </div>
          <div className="flex items-start gap-1">
            <CalendarDays className="w-4 h-4 text-[#FE5F55] mt-0.5" />
            {event.recurring_schedule?.computed_dates?.length > 0 ? (
              <div className="flex flex-col">
                {(() => {
                  const sched = event.recurring_schedule;
                  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
                  const freqLabels = { every: 'Every', every_2nd: 'Every 2nd', every_3rd: 'Every 3rd', '1st_and_3rd': '1st & 3rd', '2nd_and_4th': '2nd & 4th' };
                  const daysStr = (sched.days || []).map(d => dayNames[d]).join(', ');
                  const freqStr = freqLabels[sched.frequency] || 'Every';
                  const startStr = formatDate(event.date);
                  const endStr = event.end_date ? formatDate(event.end_date) : '';
                  return (
                    <>
                      <span className="text-emerald-700 font-medium">{freqStr} {daysStr}</span>
                      <span className="text-xs text-[#71717A]">{startStr} - {endStr}</span>
                      <span className="text-xs text-emerald-600 font-medium">{sched.computed_dates.length} market days</span>
                    </>
                  );
                })()}
              </div>
            ) : (
              <div className="flex flex-wrap gap-x-2 gap-y-1">
                {formatEventDates(event).slice(0, 2).map((d, i) => (
                  <span key={i}>{d}{i === 0 && formatEventDates(event).length > 1 ? ',' : ''}</span>
                ))}
                {formatEventDates(event).length > 2 && (
                  <span className="text-[#1E3A5F] font-medium">+{formatEventDates(event).length - 2} more</span>
                )}
              </div>
            )}
          </div>
          {event.deadline && (
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4 text-[#1E3A5F]" />
              <span>Apply by {formatDate(event.deadline)}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <DollarSign className="w-4 h-4 text-[#1E3A5F]" />
            {formatCurrency(event.booth_cost)}
          </div>
        </div>
      </div>

      {distance !== null && !isNaN(distance) && (
        <div className="flex items-center justify-center sm:justify-end">
          <div className="text-center px-4 py-2 bg-[#FFEDDF] rounded-lg border border-[#1E3A5F]">
            <p className="text-2xl font-bold text-[#1E3A5F]">{distance}</p>
            <p className="text-xs text-[#71717A]">miles</p>
          </div>
        </div>
      )}
    </Link>
  );
};

// Event Pill Component for Calendar - with priority colors per application status
export const EventPill = ({ event, type, onClick, applicationStatus, showBadge = true }) => {
  const colors = PRIORITY_COLORS[type] || PRIORITY_COLORS.community;
  
  // Override colors based on specific application status
  let displayColors = colors;
  let showPulse = false;
  let statusLabel = '';
  
  if (applicationStatus) {
    if (applicationStatus.status === 'accepted') {
      displayColors = PRIORITY_COLORS.accepted;
      statusLabel = 'Accepted';
    } else if (['draft', 'pending', 'submitted'].includes(applicationStatus.status)) {
      displayColors = PRIORITY_COLORS.applicationInProgress;
      showPulse = true;
      statusLabel = applicationStatus.status;
    } else if (applicationStatus.status === 'waitlisted') {
      displayColors = PRIORITY_COLORS.waitlisted;
      statusLabel = 'Waitlisted';
    } else if (applicationStatus.status === 'rejected') {
      displayColors = PRIORITY_COLORS.rejected;
      statusLabel = 'Rejected';
    }
  }
  
  return (
    <div 
      onClick={(e) => {
        e.stopPropagation();
        onClick && onClick(event);
      }}
      className={`
        ${displayColors.bg} ${displayColors.border} ${displayColors.text}
        border-l-4 rounded-r px-2 py-1 mb-1 cursor-pointer
        hover:opacity-80 transition-opacity truncate text-xs font-medium
        flex items-center gap-1
      `}
      title={`${event.title}${statusLabel ? ` (${statusLabel})` : ''}`}
    >
      {showPulse && (
        <span className="w-2 h-2 rounded-full bg-purple-600 flex-shrink-0 animate-pulse" />
      )}
      {applicationStatus?.status === 'accepted' && (
        <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
      )}
      {applicationStatus?.status === 'waitlisted' && (
        <AlertCircle className="w-3 h-3 flex-shrink-0" />
      )}
      {applicationStatus?.status === 'rejected' && (
        <X className="w-3 h-3 flex-shrink-0" />
      )}
      <span className="truncate">{event.title}</span>
    </div>
  );
};

// Calendar View Component - Shows one month with side panel for quick-access event list
export const CalendarView = ({ favorites, selectedDate, setSelectedDate, eventDates, getEventsForDate, getDistance, fetchFavorites, favoriteIds, applicationStatusMap = {} }) => {
  // Find the earliest upcoming event to determine initial display month
  const initialDisplayMonth = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Find events with valid dates that are today or in the future
    const upcomingEvents = favorites
      .filter(e => e.date)
      .map(e => {
        const [year, month, day] = e.date.split('-').map(Number);
        return new Date(year, month - 1, day);
      })
      .filter(d => d >= today)
      .sort((a, b) => a - b);
    
    // If there are upcoming events, use the first one's month
    if (upcomingEvents.length > 0) {
      return upcomingEvents[0];
    }
    
    // Otherwise, default to current month
    return new Date();
  }, [favorites]);
  
  const [displayMonth, setDisplayMonth] = useState(initialDisplayMonth);
  const [hoveredDate, setHoveredDate] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Update displayMonth when favorites change (e.g., switching tabs)
  useEffect(() => {
    setDisplayMonth(initialDisplayMonth);
  }, [initialDisplayMonth]);

  const selectedDateEvents = getEventsForDate(selectedDate);
  const hasEventsOnSelectedDate = selectedDateEvents.length > 0;
  
  // Get events for the currently displayed month
  const currentMonthStart = new Date(displayMonth.getFullYear(), displayMonth.getMonth(), 1);
  const currentMonthEnd = new Date(displayMonth.getFullYear(), displayMonth.getMonth() + 1, 0);
  const currentMonthEvents = favorites
    .filter(e => {
      if (!e.date) return false;  // Skip events without dates
      const eventDate = new Date(e.date + 'T00:00:00');
      return eventDate >= currentMonthStart && eventDate <= currentMonthEnd;
    })
    .sort((a, b) => new Date(a.date + 'T00:00:00') - new Date(b.date + 'T00:00:00'));

  // Helper to check if a date falls within an event's date range
  const dateInRange = (dateStr, startDate, endDate) => {
    if (!startDate) return false;
    if (!endDate) return dateStr === startDate;
    
    const [year, month, day] = dateStr.split('-').map(Number);
    const checkDate = new Date(year, month - 1, day);
    const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
    const start = new Date(startYear, startMonth - 1, startDay);
    const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
    const end = new Date(endYear, endMonth - 1, endDay);
    
    return checkDate >= start && checkDate <= end;
  };

  // Helper to get all dates between start and end (inclusive)
  const getDateRangeArray = (startStr, endStr) => {
    const dates = [];
    if (!startStr) return dates;
    
    const [startYear, startMonth, startDay] = startStr.split('-').map(Number);
    const start = new Date(startYear, startMonth - 1, startDay);
    
    if (!endStr) {
      dates.push(start);
      return dates;
    }
    
    const [endYear, endMonth, endDay] = endStr.split('-').map(Number);
    const end = new Date(endYear, endMonth - 1, endDay);
    
    const current = new Date(start);
    while (current <= end) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  // Separate favorited and non-favorited event dates for different styling
  const { favoritedEventDates, nonFavoritedEventDates } = useMemo(() => {
    const favorited = [];
    const nonFavorited = [];
    
    favorites.forEach(e => {
      const isFavorited = favoriteIds && favoriteIds.has(e.id);
      const targetArray = isFavorited ? favorited : nonFavorited;
      
      // For recurring events, use computed_dates instead of full range
      if (e.recurring_schedule?.computed_dates?.length > 0) {
        e.recurring_schedule.computed_dates.forEach(dateStr => {
          const [y, m, d] = dateStr.split('-').map(Number);
          targetArray.push(new Date(y, m - 1, d));
        });
        return;
      }
      
      // Add all dates in primary range
      getDateRangeArray(e.date, e.end_date).forEach(d => targetArray.push(d));
      
      // Add all dates from additional date ranges
      if (e.event_dates && Array.isArray(e.event_dates)) {
        e.event_dates.forEach(range => {
          getDateRangeArray(range.start_date, range.end_date).forEach(d => targetArray.push(d));
        });
      }
    });
    
    return { favoritedEventDates: favorited, nonFavoritedEventDates: nonFavorited };
  }, [favorites, favoriteIds]);

  // Get deadline dates for calendar highlighting with event info
  const deadlineMap = useMemo(() => {
    const map = {};
    favorites.forEach(e => {
      if (e.deadline) {
        const deadlineStr = e.deadline.split('T')[0];
        if (!map[deadlineStr]) map[deadlineStr] = [];
        map[deadlineStr].push({ ...e, isDeadline: true, isFavorited: favoriteIds && favoriteIds.has(e.id) });
      }
    });
    return map;
  }, [favorites, favoriteIds]);

  // Get all events/deadlines for a specific date string (including date ranges)
  const getEventsForDateStr = (dateStr) => {
    const events = [];
    
    // Check each event
    favorites.forEach(e => {
      const isFavorited = favoriteIds && favoriteIds.has(e.id);
      
      // For events with recurring_schedule.computed_dates, ONLY match computed dates
      if (e.recurring_schedule?.computed_dates?.length > 0) {
        if (e.recurring_schedule.computed_dates.includes(dateStr)) {
          const isStartDate = e.date === dateStr;
          events.push({ ...e, isStartDate, isDeadline: false, isFavorited });
        }
        // Skip normal date range check for recurring events
      } else {
        // Check if date is within primary date range
        if (dateInRange(dateStr, e.date, e.end_date)) {
          const isStartDate = e.date === dateStr;
          events.push({ ...e, isStartDate, isDeadline: false, isFavorited });
        }
        
        // Check additional date ranges
        if (e.event_dates && Array.isArray(e.event_dates)) {
          e.event_dates.forEach(range => {
            if (dateInRange(dateStr, range.start_date, range.end_date)) {
              const isStartDate = range.start_date === dateStr;
              if (!events.find(ev => ev.id === e.id && !ev.isDeadline)) {
                events.push({ ...e, isStartDate, isDeadline: false, isFavorited });
              }
            }
          });
        }
      }
    });
    
    // Add deadline events
    const deadlineEvents = deadlineMap[dateStr] || [];
    
    return [...events, ...deadlineEvents];
  };

  const deadlineDates = favorites
    .filter(e => e.deadline)
    .map(e => {
      const [year, month, day] = e.deadline.split('T')[0].split('-').map(Number);
      return new Date(year, month - 1, day);
    });

  const calendarModifiers = {
    hasFavoritedEvent: favoritedEventDates,
    hasNonFavoritedEvent: nonFavoritedEventDates,
    hasDeadline: deadlineDates
  };

  const calendarModifiersStyles = {
    hasFavoritedEvent: {
      backgroundColor: '#1E3A5F',
      color: '#1E3A5F',
      fontWeight: 'bold',
      borderRadius: '50%'
    },
    hasNonFavoritedEvent: {
      backgroundColor: '#1E3A5F',
      color: 'white',
      fontWeight: 'bold',
      borderRadius: '4px'  // Square-ish for non-favorited
    },
    hasDeadline: {
      border: '2px solid #FE5F55',
      borderRadius: '50%'
    }
  };

  // Handle mouse events for tooltip
  const handleDayMouseEnter = (date, e) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    const eventsOnDate = getEventsForDateStr(dateStr);
    if (eventsOnDate.length > 0) {
      setHoveredDate({ date: dateStr, events: eventsOnDate });
      const rect = e.target.getBoundingClientRect();
      setTooltipPosition({ 
        x: rect.left + rect.width / 2, 
        y: rect.top - 10 
      });
    }
  };

  const handleDayMouseLeave = () => {
    setHoveredDate(null);
  };

  // Format month name for header
  const monthName = displayMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="card-dashboard relative">
      {/* Hover Tooltip */}
      {hoveredDate && (
        <div 
          className="fixed z-50 bg-white border-2 border-[#1E3A5F] rounded-xl shadow-hard p-3 max-w-xs pointer-events-auto"
          style={{ 
            left: tooltipPosition.x, 
            top: tooltipPosition.y,
            transform: 'translate(-50%, -100%)'
          }}
          onMouseEnter={() => {}} // Keep tooltip visible when hovering over it
          onMouseLeave={handleDayMouseLeave}
        >
          <div className="space-y-2">
            {hoveredDate.events.slice(0, 3).map((event, idx) => {
              const appStatus = applicationStatusMap[event.id];
              return (
              <Link 
                key={`${event.id}-${idx}`}
                to={`/events/${event.id}`}
                className="block hover:bg-[#FFEDDF] rounded p-2 -m-1 transition-colors"
              >
                <div className="flex items-start gap-2">
                  {event.isDeadline ? (
                    <Clock className="w-4 h-4 text-[#FE5F55] mt-0.5 flex-shrink-0" />
                  ) : appStatus?.status === 'accepted' ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  ) : appStatus?.status === 'waitlisted' ? (
                    <AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                  ) : appStatus?.status === 'rejected' ? (
                    <X className="w-4 h-4 text-rose-600 mt-0.5 flex-shrink-0" />
                  ) : appStatus && ['draft', 'pending', 'submitted'].includes(appStatus.status) ? (
                    <span className="w-4 h-4 mt-0.5 flex-shrink-0 flex items-center justify-center">
                      <span className="w-2.5 h-2.5 rounded-full bg-purple-600 animate-pulse" />
                    </span>
                  ) : event.isFavorited ? (
                    <Heart className="w-4 h-4 text-[#1E3A5F] mt-0.5 flex-shrink-0 fill-[#1E3A5F]" />
                  ) : (
                    <Square className="w-4 h-4 text-[#1E3A5F] mt-0.5 flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-[#1E3A5F] line-clamp-1">{event.title}</p>
                    <p className="text-xs text-[#4A4A4A]">
                      {event.city && event.state ? `${event.city}, ${event.state}` : ''}
                    </p>
                    <p className="text-xs font-medium mt-0.5">
                      {event.isDeadline ? (
                        <span className="text-[#FE5F55]">Application Deadline</span>
                      ) : appStatus ? (
                        <span className={
                          appStatus.status === 'accepted' ? 'text-emerald-600' :
                          appStatus.status === 'waitlisted' ? 'text-orange-600' :
                          appStatus.status === 'rejected' ? 'text-rose-600' :
                          'text-purple-600'
                        }>
                          {appStatus.status.charAt(0).toUpperCase() + appStatus.status.slice(1)}
                        </span>
                      ) : event.isStartDate ? (
                        <span className="text-[#1E3A5F]">Event Starts</span>
                      ) : (
                        <span className="text-[#71717A]">Event Day</span>
                      )}
                    </p>
                  </div>
                </div>
              </Link>
              );
            })}
            {hoveredDate.events.length > 3 && (
              <p className="text-xs text-[#71717A] text-center">+{hoveredDate.events.length - 3} more</p>
            )}
          </div>
          {/* Tooltip arrow */}
          <div className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-full">
            <div className="border-8 border-transparent border-t-[#1E3A5F]" />
          </div>
        </div>
      )}

      <div className="flex flex-col gap-6">
        {/* Custom Calendar Grid with Event Pills */}
        <div className="relative bg-white rounded-xl border border-[#E5E5E5] overflow-hidden">
          {/* Calendar Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] bg-[#FAFAFA]">
            <button
              onClick={() => setDisplayMonth(new Date(displayMonth.getFullYear(), displayMonth.getMonth() - 1, 1))}
              className="p-2 hover:bg-[#E5E5E5] rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-[#4A4A4A]" />
            </button>
            <h2 className="text-xl font-bold font-['Outfit'] text-[#000501]">
              {displayMonth.toLocaleDateString('en-US', { month: 'long' })} <span className="font-normal text-[#71717A]">{displayMonth.getFullYear()}</span>
            </h2>
            <button
              onClick={() => setDisplayMonth(new Date(displayMonth.getFullYear(), displayMonth.getMonth() + 1, 1))}
              className="p-2 hover:bg-[#E5E5E5] rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-[#4A4A4A]" />
            </button>
          </div>

          {/* Day Headers - Sunday to Saturday */}
          <div className="grid grid-cols-7 border-b border-[#E5E5E5]">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="px-2 py-3 text-center text-sm font-medium text-[#71717A] border-r border-[#E5E5E5] last:border-r-0">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7">
            {(() => {
              const year = displayMonth.getFullYear();
              const month = displayMonth.getMonth();
              const firstDay = new Date(year, month, 1);
              const lastDay = new Date(year, month + 1, 0);
              const startDayOfWeek = firstDay.getDay(); // Sunday = 0 (week starts on Sunday)
              const daysInMonth = lastDay.getDate();
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              
              const cells = [];
              
              // Previous month days (fill days before the 1st)
              const prevMonth = new Date(year, month, 0);
              const prevMonthDays = prevMonth.getDate();
              for (let i = startDayOfWeek - 1; i >= 0; i--) {
                const day = prevMonthDays - i;
                cells.push(
                  <div key={`prev-${day}`} className="min-h-[120px] p-2 border-r border-b border-[#E5E5E5] bg-[#FAFAFA]/50">
                    <span className="text-sm text-[#C0C0C0]">{day}</span>
                  </div>
                );
              }
              
              // Current month days
              for (let day = 1; day <= daysInMonth; day++) {
                const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const currentDate = new Date(year, month, day);
                const isToday = currentDate.getTime() === today.getTime();
                const isSelected = selectedDate && 
                  selectedDate.getDate() === day && 
                  selectedDate.getMonth() === month && 
                  selectedDate.getFullYear() === year;
                
                // Get events for this date
                const eventsOnDate = getEventsForDateStr(dateStr);
                const eventPills = eventsOnDate.slice(0, 3); // Max 3 pills
                const moreCount = eventsOnDate.length - 3;
                
                cells.push(
                  <div 
                    key={`day-${day}`}
                    onClick={() => setSelectedDate(new Date(year, month, day))}
                    className={`
                      min-h-[120px] p-2 border-r border-b border-[#E5E5E5] cursor-pointer
                      transition-colors hover:bg-[#F5F5F5]
                      ${isSelected ? 'bg-[#1E3A5F]/10 ring-2 ring-inset ring-[#1E3A5F]' : ''}
                      ${isToday ? 'bg-[#FFEDDF]/30' : ''}
                    `}
                  >
                    <div className={`
                      text-sm font-medium mb-2 inline-flex items-center justify-center w-7 h-7 rounded-full
                      ${isToday ? 'bg-[#1E3A5F] text-white' : 'text-[#000501]'}
                    `}>
                      {day}
                    </div>
                    
                    {/* Event Pills */}
                    <div className="space-y-1">
                      {eventPills.map((event, idx) => {
                        const appStatus = applicationStatusMap[event.id];
                        let pillType = event.isDeadline ? 'deadline' : 
                          (event.isFavorited ? 'saved' : event.is_community ? 'community' : 'myEvent');
                        // Override with application status type
                        if (appStatus) {
                          if (appStatus.status === 'accepted') pillType = 'accepted';
                          else if (['draft', 'pending', 'submitted'].includes(appStatus.status)) pillType = 'applicationInProgress';
                          else if (appStatus.status === 'waitlisted') pillType = 'waitlisted';
                          else if (appStatus.status === 'rejected') pillType = 'rejected';
                        }
                        return (
                          <EventPill 
                            key={`${event.id}-${idx}`}
                            event={event}
                            type={pillType}
                            applicationStatus={appStatus || null}
                            onClick={() => window.location.href = `/events/${event.id}`}
                          />
                        );
                      })}
                      {moreCount > 0 && (
                        <div className="text-xs text-[#71717A] pl-2">+{moreCount} more</div>
                      )}
                    </div>
                  </div>
                );
              }
              
              // Next month days to fill the grid
              const totalCells = cells.length;
              const remainingCells = (7 - (totalCells % 7)) % 7;
              for (let i = 1; i <= remainingCells; i++) {
                cells.push(
                  <div key={`next-${i}`} className="min-h-[120px] p-2 border-r border-b border-[#E5E5E5] bg-[#FAFAFA]/50">
                    <span className="text-sm text-[#C0C0C0]">{i}</span>
                  </div>
                );
              }
              
              return cells;
            })()}
          </div>

          {/* Legend */}
          <div className="px-6 py-4 border-t border-[#E5E5E5] bg-[#FAFAFA] flex flex-wrap gap-4">
            <div className="flex items-center gap-2 text-xs text-[#4A4A4A]">
              <div className={`w-4 h-3 rounded-r ${PRIORITY_COLORS.accepted.bg} border-l-4 ${PRIORITY_COLORS.accepted.border} flex items-center justify-center`}>
                <CheckCircle2 className="w-2.5 h-2.5 text-emerald-600" />
              </div>
              <span className="font-medium text-emerald-700">Accepted</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#4A4A4A]">
              <div className={`w-4 h-3 rounded-r ${PRIORITY_COLORS.applicationInProgress.bg} border-l-4 ${PRIORITY_COLORS.applicationInProgress.border} flex items-center justify-center`}>
                <span className="w-1.5 h-1.5 rounded-full bg-purple-600 animate-pulse" />
              </div>
              <span className="font-medium text-purple-700">In Progress</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#4A4A4A]">
              <div className={`w-4 h-3 rounded-r ${PRIORITY_COLORS.waitlisted.bg} border-l-4 ${PRIORITY_COLORS.waitlisted.border}`} />
              <span className="font-medium text-orange-700">Waitlisted</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#4A4A4A]">
              <div className={`w-4 h-3 rounded-r ${PRIORITY_COLORS.rejected.bg} border-l-4 ${PRIORITY_COLORS.rejected.border}`} />
              <span className="font-medium text-rose-700">Rejected</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#4A4A4A]">
              <div className={`w-4 h-3 rounded-r ${PRIORITY_COLORS.saved.bg} border-l-4 ${PRIORITY_COLORS.saved.border}`} />
              <span className="font-medium text-red-600">Saved Events</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#4A4A4A]">
              <div className={`w-4 h-3 rounded-r ${PRIORITY_COLORS.myEvent.bg} border-l-4 ${PRIORITY_COLORS.myEvent.border}`} />
              <span className="font-medium text-gray-600">My Events</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#4A4A4A]">
              <div className={`w-4 h-3 rounded-r ${PRIORITY_COLORS.community.bg} border-l-4 ${PRIORITY_COLORS.community.border}`} />
              <span className="font-medium text-cyan-700">Community Events</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#4A4A4A]">
              <div className={`w-4 h-3 rounded-r ${PRIORITY_COLORS.deadline.bg} border-l-4 ${PRIORITY_COLORS.deadline.border}`} />
              <span className="font-medium text-[#c44a42]">Deadline Soon</span>
            </div>
          </div>
        </div>

        {/* Side Panel - Quick Access Event List */}
        <div className="bg-[#FFEDDF]/50 rounded-xl p-4 border border-[#E5E5E5] max-h-[400px] overflow-y-auto">
          {hasEventsOnSelectedDate ? (
            <>
              <h3 className="text-base font-bold font-['Outfit'] text-[#1E3A5F] mb-3 sticky top-0 bg-[#FFEDDF]/90 py-2 -mt-2 -mx-2 px-2 backdrop-blur-sm">
                {formatDate(selectedDate.toISOString())}
              </h3>
              <div className="space-y-3">
                {selectedDateEvents.map((event) => (
                  <CompactEventItem 
                    key={event.id} 
                    event={event} 
                    distance={getDistance(event)}
                    isFavorited={favoriteIds && favoriteIds.has(event.id)}
                  />
                ))}
              </div>
            </>
          ) : (
            <>
              <h3 className="text-base font-bold font-['Outfit'] text-[#1E3A5F] mb-1 sticky top-0 bg-[#FFEDDF]/90 py-2 -mt-2 -mx-2 px-2 backdrop-blur-sm">
                {monthName}
              </h3>
              {currentMonthEvents.length > 0 ? (
                <>
                  <p className="text-xs text-[#71717A] mb-3">
                    {currentMonthEvents.length} event{currentMonthEvents.length !== 1 ? 's' : ''} this month
                  </p>
                  <div className="space-y-3">
                    {currentMonthEvents.map((event) => (
                      <CompactEventItem 
                        key={event.id} 
                        event={event} 
                        distance={getDistance(event)}
                        onClick={() => setSelectedDate(new Date(event.date + 'T00:00:00'))}
                        isFavorited={favoriteIds && favoriteIds.has(event.id)}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <CalendarDays className="w-10 h-10 text-[#71717A]/50 mx-auto mb-2" />
                  <p className="text-sm text-[#71717A]">No events this month</p>
                  {favorites.length > 0 && (() => {
                    // Find the next event after this month
                    const nextEvent = favorites
                      .filter(e => e.date && new Date(e.date + 'T00:00:00') > currentMonthEnd)
                      .sort((a, b) => new Date(a.date + 'T00:00:00') - new Date(b.date + 'T00:00:00'))[0];
                    if (nextEvent) {
                      const nextDate = new Date(nextEvent.date + 'T00:00:00');
                      return (
                        <button
                          onClick={() => setDisplayMonth(new Date(nextDate.getFullYear(), nextDate.getMonth(), 1))}
                          className="text-xs text-[#1E3A5F] hover:underline mt-2 inline-flex items-center gap-1"
                        >
                          <ChevronRight className="w-3 h-3" />
                          Next event: {nextDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </button>
                      );
                    }
                    return null;
                  })()}
                  <Link to="/ai-discover" className="text-xs text-[#1E3A5F] hover:underline mt-2 block">
                    Discover events
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Upcoming Events List Below */}
      {favorites.length > 0 && (
        <div className="mt-8 pt-6 border-t-2 border-[#E5E5E5]">
          <h3 className="text-xl font-bold font-['Outfit'] text-[#1E3A5F] mb-4">
            All Upcoming Events
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {sortEventsByPriority(
              favorites.filter(e => e.date && new Date(e.date + 'T00:00:00') >= new Date()),
              applicationStatusMap,
              favoriteIds
            ).map((event) => (
                <EventListItem 
                  key={event.id} 
                  event={event} 
                  distance={getDistance(event)}
                  onFavoriteChange={fetchFavorites}
                  applicationStatus={applicationStatusMap[event.id]}
                  isFavorited={favoriteIds?.has(event.id)}
                />
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Compact Event Item for Side Panel
export const CompactEventItem = ({ event, distance, onClick, isFavorited }) => {
  const eventType = EVENT_TYPES[event.event_type] || { label: event.event_type, color: 'gray' };
  
  return (
    <Link 
      to={`/events/${event.id}`}
      onClick={onClick}
      className="block p-3 bg-white rounded-lg border border-[#E5E5E5] hover:border-[#1E3A5F] hover:shadow-sm transition-all"
      data-testid={`compact-event-${event.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 min-w-0">
          {isFavorited ? (
            <Heart className="w-4 h-4 text-[#1E3A5F] fill-[#1E3A5F] flex-shrink-0" />
          ) : (
            <Square className="w-4 h-4 text-[#1E3A5F] flex-shrink-0" />
          )}
          <h4 className="font-semibold text-sm text-[#1E3A5F] line-clamp-1">{event.title}</h4>
        </div>
        {distance !== null && !isNaN(distance) && (
          <span className="text-xs text-[#71717A] whitespace-nowrap">{distance}mi</span>
        )}
      </div>
      <div className="flex items-center gap-2 text-xs text-[#4A4A4A] ml-6">
        <CalendarDays className="w-3 h-3 text-[#FE5F55]" />
        <span>{formatDate(event.date)}</span>
      </div>
      <div className="flex items-center gap-2 text-xs text-[#4A4A4A] mt-1 ml-6">
        <MapPin className="w-3 h-3 text-[#FE5F55]" />
        <span className="line-clamp-1">{event.city}, {event.state}</span>
      </div>
    </Link>
  );
};

export default CalendarView;
